import { condition, type FilterNode } from '@ofmos/shared/filter-ast';
import { type OrderBy, RECORD_QUERY_MAX_LIMIT } from '@ofmos/shared/record-query';
import { scopeFor } from '@ofmos/shared/roles';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';
import type { FieldMetadata, FieldValue, ObjectMetadata, RecordRow } from '../../../data/client';
import {
  isFullRecord,
  useFields,
  useInboundRelations,
  useListedRecords,
  useObjects,
  useRecordList,
  useRecordsByIds,
} from '../../../data/hooks';
import { queryKeys } from '../../../data/queryKeys';
import { useRelationLabel } from '../../../fields/useRelationLabel';
import { useSession } from '../../auth/useSession';
import { isMoney, type Money } from './clientModel';

/**
 * RESOLUTION BY API NAME AGAINST RUNTIME METADATA (common contract section 2; the pattern of
 * `dashboard/usePanels.ts`). An object that is not in this installation, or a field it does
 * not carry, makes the thing that needs it ABSENT - a column, a section, a line - never a
 * crash and never a query with an empty field id in it.
 */
export interface NamedObject {
  readonly object: ObjectMetadata | undefined;
  readonly fields: ReadonlyMap<string, FieldMetadata>;
  readonly isPending: boolean;
  /**
   * The metadata read failed with nothing to show (DESIGN.md H7): the object is unknown because
   * the read failed, not because the installation lacks it, so a page says it could not load
   * instead of its empty state.
   */
  readonly isError: boolean;
}

export function useNamedObject(name: string, enabled = true): NamedObject {
  return useObjectWhere((entry) => entry.name === name, enabled);
}

/** The same resolution for an object known by id, such as a junction a field points at. */
export function useObjectById(id: string | undefined): NamedObject {
  return useObjectWhere((entry) => entry.id === id, id !== undefined);
}

/**
 * The same object while its metadata is the same (BR-PERF): every join on the Clients list is
 * memoised on these, and a new map per render would recompute all of them on every render.
 */
function useObjectWhere(match: (object: ObjectMetadata) => boolean, enabled: boolean): NamedObject {
  const objects = useObjects();
  const object = enabled ? objects.data?.find(match) : undefined;
  const fields = useFields(object?.id ?? '', object !== undefined);
  const isPending = objects.isPending || (object !== undefined && fields.isPending);
  const isError =
    (enabled && objects.isError && objects.data === undefined) ||
    (object !== undefined && fields.isError && fields.data === undefined);
  const list = fields.data;
  return useMemo(
    () => ({
      object,
      fields: new Map((list ?? []).map((field) => [field.name, field])),
      isPending,
      isError,
    }),
    [object, list, isPending, isError],
  );
}

/** The ids of the named fields that exist, in the given order, for a `select`. */
export function selectOf(named: NamedObject, names: readonly string[]): readonly string[] {
  const ids = names
    .map((name) => named.fields.get(name)?.id)
    .filter((id): id is string => id !== undefined);
  const label = named.object?.labelIdentifierFieldId;
  return label !== undefined && label !== '' && !ids.includes(label) ? [label, ...ids] : ids;
}

/**
 * WHETHER THIS PERSON MAY READ AN OBJECT'S RECORDS. The Coach role loses read on payments
 * and deals in this wave (P1), and Billing and the Open column go with it (concept 4.4, 4.5
 * item 9). An object the server does not list is not readable either.
 */
export function useCanRead(object: ObjectMetadata | undefined): boolean {
  const session = useSession();
  if (object === undefined) return false;
  const permissions = session.data?.permissions;
  if (permissions === undefined) return true;
  return scopeFor(permissions, object.id, 'read') !== 'none';
}

// --- reading one value by field NAME ----------------------------------------

export function fieldValueOf(
  row: RecordRow | null | undefined,
  named: NamedObject,
  name: string,
): FieldValue | undefined {
  const field = named.fields.get(name);
  if (row === null || row === undefined || field === undefined) return undefined;
  return row.values[field.id];
}

export function textOf(row: RecordRow | null | undefined, named: NamedObject, name: string) {
  const value = fieldValueOf(row, named, name);
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

export function numberOf(row: RecordRow | null | undefined, named: NamedObject, name: string) {
  const value = fieldValueOf(row, named, name);
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function moneyOf(
  row: RecordRow | null | undefined,
  named: NamedObject,
  name: string,
): Money | null {
  const value = fieldValueOf(row, named, name);
  return isMoney(value) ? value : null;
}

export function booleanOf(row: RecordRow | null | undefined, named: NamedObject, name: string) {
  const value = fieldValueOf(row, named, name);
  return typeof value === 'boolean' ? value : null;
}

/** A SELECT value's option LABEL, which is what every rule in this package compares. */
export function optionLabelOf(
  row: RecordRow | null | undefined,
  named: NamedObject,
  name: string,
): string | null {
  const field = named.fields.get(name);
  const value = fieldValueOf(row, named, name);
  if (field === undefined || typeof value !== 'string') return null;
  return field.options?.selectOptions?.find((option) => option.id === value)?.label ?? null;
}

/** A MANY_TO_ONE relation's target id. */
export function relationIdOf(
  row: RecordRow | null | undefined,
  named: NamedObject,
  name: string,
): string | null {
  const value = fieldValueOf(row, named, name);
  return typeof value === 'string' && value !== '' ? value : null;
}

/** A MANY_TO_ONE relation's target label, as the list query resolved it. */
export function relationLabelOf(
  row: RecordRow | null | undefined,
  named: NamedObject,
  name: string,
): string | null {
  const field = named.fields.get(name);
  if (row === null || row === undefined || field === undefined) return null;
  return row.relationLabels?.[field.id] ?? null;
}

/** The record's title, from the object's label field. */
export function titleOf(row: RecordRow | null | undefined, named: NamedObject): string {
  const id = named.object?.labelIdentifierFieldId;
  const value = id === undefined || row === null || row === undefined ? undefined : row.values[id];
  return typeof value === 'string' && value.trim() !== '' ? value : 'Untitled';
}

/**
 * THE AGENCY'S PEOPLE JUNCTION (`agency.people`, a MANY_TO_MANY, INV-9). The junction is a
 * system object whose `source` field points at the agency and `target` at the person.
 */
export function peopleJunctionId(agency: NamedObject): string | undefined {
  return agency.fields.get('people')?.options?.relation?.junctionObjectId;
}

/**
 * THE JUNCTION WITH ITS TWO ENDPOINTS. The endpoints are SYSTEM fields, which no field list
 * hands out (`isDisplayableField`, and the fixture's `fieldsOf` says why); the record page's
 * related sections find them as the inbound relations of the two objects they point at
 * (`related/sectionModel.ts`, `endpointFieldsOf`), and so does this.
 */
export function usePeopleJunction(agency: NamedObject, person: NamedObject): NamedObject {
  const junctionId = peopleJunctionId(agency);
  const junction = useObjectById(junctionId);
  const fromAgency = useInboundRelations(agency.object?.id ?? '', junctionId !== undefined);
  const fromPerson = useInboundRelations(person.object?.id ?? '', junctionId !== undefined);
  const source = fromAgency.data?.find(
    (relation) => relation.object.id === junctionId && relation.field.name === 'source',
  )?.field;
  const target = fromPerson.data?.find(
    (relation) => relation.object.id === junctionId && relation.field.name === 'target',
  )?.field;
  const isPending =
    junction.isPending ||
    (junctionId !== undefined && (fromAgency.isPending || fromPerson.isPending));
  return useMemo(() => {
    const fields = new Map(junction.fields);
    if (source !== undefined) fields.set('source', source);
    if (target !== undefined) fields.set('target', target);
    return { object: junction.object, fields, isPending, isError: junction.isError };
  }, [junction, source, target, isPending]);
}

export interface RowsQuery {
  readonly select: readonly string[];
  readonly filter?: FilterNode | undefined;
  readonly orderBy?: readonly OrderBy[] | undefined;
  readonly limit?: number | undefined;
  readonly enabled?: boolean | undefined;
  /** The server's count of the whole filtered set with the same request (DESIGN.md F16, G13). */
  readonly withTotalCount?: boolean | undefined;
}

export interface Rows {
  readonly rows: readonly RecordRow[];
  readonly isPending: boolean;
  readonly isError: boolean;
  /**
   * The list had answered and a later read of it failed: the rows are the last answer, still
   * usable (DESIGN.md H7, "keep loaded rows on a load error").
   */
  readonly isRefetchError: boolean;
  /** The server's count when the query asked for it (`withTotalCount`), else null. */
  readonly total: number | null;
  /** Asks the list again: the "Try again" of a failed load (handbook H7, D13). */
  readonly refetch: () => void;
}

const NO_IDS: readonly string[] = [];
const NO_ROWS: readonly RecordRow[] = [];

/**
 * ONE LIST QUERY, READ AS ROWS (BR-PERF). The list caches ids and seeds each record's canonical
 * key (INV-13), and `useListedRecords` reads those keys with one cache subscription for the whole
 * list: the list's one request is the only one, on the first load, on a stale return and on a
 * change notice alike, and no row is ever fetched on its own. Disabled - an absent object, or a
 * filter that cannot be built yet - it is an empty, settled answer. The rows keep their identity
 * while their records do, so a join memoised on them reruns only when an answer changed them.
 */
export function useRows(named: NamedObject, query: RowsQuery): Rows {
  const objectId = named.object?.id ?? '';
  /* Not before the fields are in: until then `selectOf` holds the label field alone, and that
     narrower list was a call of its own on every cold load. */
  const enabled =
    (query.enabled ?? true) &&
    named.object !== undefined &&
    !named.isPending &&
    query.select.length > 0;
  const list = useRecordList(objectId, {
    select: query.select,
    ...(query.filter === undefined ? {} : { filter: query.filter }),
    ...(query.orderBy === undefined ? {} : { orderBy: query.orderBy }),
    limit: query.limit ?? RECORD_QUERY_MAX_LIMIT,
    ...(query.withTotalCount === true ? { withTotalCount: true } : {}),
    enabled,
  });
  const listed = useListedRecords(objectId, enabled ? (list.data?.ids ?? NO_IDS) : NO_IDS, list);
  /* A list that failed and is being asked again ("Try again") is back to `pending` without data
     in TanStack; it stays a failure until the new answer is in, so a page never takes it for a
     first load and a section never shows its empty line meanwhile (DESIGN.md H7). */
  const retrying = list.isPending && list.errorUpdateCount > 0;
  const isPending = enabled && (list.isPending || listed.isPending) && !retrying;
  const isError = enabled && (list.isError || retrying);
  const isRefetchError = enabled && list.isRefetchError;
  const total = enabled ? (list.data?.totalCount ?? null) : null;
  const rows = enabled ? listed.records : NO_ROWS;
  const { refetch: again } = list;
  const refetch = useCallback(() => void again(), [again]);
  return useMemo(
    () => ({ rows, isPending, isError, isRefetchError, total, refetch }),
    [rows, isPending, isError, isRefetchError, total, refetch],
  );
}

/** A condition on a field by NAME; undefined when the field is absent, so the query is too. */
export function whereField(
  named: NamedObject,
  name: string,
  operator: 'eq' | 'in',
  value: unknown,
): FilterNode | undefined {
  const field = named.fields.get(name);
  return field === undefined ? undefined : condition(field.id, operator, value);
}

/** Newest first by a field NAME, when the field exists. */
export function newestFirst(named: NamedObject, name: string): readonly OrderBy[] | undefined {
  const field = named.fields.get(name);
  return field === undefined
    ? undefined
    : [{ fieldId: field.id, direction: 'desc', nulls: 'last' }];
}

/**
 * ONE RECORD WITH EVERY VALUE ON IT. A list query seeds the canonical key with only the fields
 * it selected, and that partial entry is fresh for the session's stale time - so a page that
 * reads fields no list asked for (the situation, the checkpoints) marks the entry stale once
 * on arrival and lets the one canonical read refill it. Same key, same read (INV-13); a later
 * list seed MERGES its values into the full row rather than replacing it (`seedCanonicalRecord`).
 *
 * ONLY A PARTIAL ENTRY IS READ AGAIN (BR-PERF): an entry a full read already filled
 * (`isFullRecord`) is the whole record, so opening the client a second time asks for nothing
 * until a write has replaced it or its stale time has passed. An entry that is not there yet,
 * or is being read, is the observer's own fetch and is left alone.
 */
export function useFullRecord(
  objectId: string | undefined,
  recordId: string | null,
): {
  readonly row: RecordRow | null;
  readonly isPending: boolean;
  /** The read failed and there is no row to show (DESIGN.md H7). */
  readonly isError: boolean;
  /** A later read failed and the row is the last answer, still shown. */
  readonly isRefetchError: boolean;
} {
  const queryClient = useQueryClient();
  const ids = useMemo(
    () => (objectId === undefined || recordId === null || recordId === '' ? NO_IDS : [recordId]),
    [objectId, recordId],
  );
  const read = useRecordsByIds(objectId ?? '', ids);
  useEffect(() => {
    if (objectId === undefined || recordId === null || recordId === '') return;
    const key = queryKeys.record(objectId, recordId);
    const entry = queryClient.getQueryState<RecordRow | null>(key);
    if (entry?.data === undefined || entry.fetchStatus === 'fetching') return;
    if (isFullRecord(entry.data)) return;
    void queryClient.invalidateQueries({ queryKey: key, exact: true });
  }, [objectId, recordId, queryClient]);
  return {
    row: read.records[0] ?? null,
    isPending: read.isPending,
    isError: read.isError,
    isRefetchError: read.isRefetchError,
  };
}

/**
 * A MANY_TO_ONE TARGET'S NAME ON A SINGLE-RECORD READ. A list query resolves relation labels
 * in SQL; the single-record read the client page makes returns bare ids, so the name comes
 * from the product's own resolver (`useRelationLabel`, the record page's) when the row did
 * not arrive with it.
 */
export function useRelationName(
  row: RecordRow | null | undefined,
  named: NamedObject,
  name: string,
): string | null {
  const carried = relationLabelOf(row, named, name);
  const resolved = useRelationLabel(
    carried === null ? named.fields.get(name) : undefined,
    relationIdOf(row, named, name),
  );
  return carried ?? resolved.label ?? null;
}
